import React, { useState, useEffect } from 'react';

// Material ui
import { Grid, IconButton, Typography } from '@material-ui/core';

// Components
import SelectInput, { SelectOption } from '@components/UI-KIT/Inputs/SelectInput';

// Icons
import { ChevronBigLeft, ChevronBigRight } from '@/assets/icons';

// Variablaes
import VARIABLES from '@/assets/variables';

interface Props {
    page: number;
    size: number;
    handlePageChange: (_value: number) => void;
    handleSizeChange: (_value: number) => void;
    rowsPerPage: number[];
    totalCount: number;
}

const Pagination: React.FC<Props> = ({
    page,
    size,
    handlePageChange,
    handleSizeChange,
    rowsPerPage,
    totalCount
}) => {
    const [ displayingPages, setDisplayingPages ] = useState<number[]>([]);

    useEffect(() => {
        const calculateDisplayingPages = (pages: number) => {
            const result = [];
            if (page >= pages - 2) {
                let count = Math.max(1, pages - 4);
                while (result.length < 5 && result.length < pages) {
                    result.push(count);
                    count++;
                }
            } else if (page <= 2) {
                let count = 1;
                while (result.length < 5 && result.length < pages) {
                    result.push(count);
                    count++;
                }
            } else {
                result.push(page - 2, page - 1, page);
                let count = page + 1;
                while (result.length < 5 && result.length < pages) {
                    result.push(count);
                    count++;
                }
            }
            return result;
        };
        const pages = totalCount % size === 0
            ? Math.floor(totalCount / size)
            : Math.floor(totalCount / size) + 1;
        setDisplayingPages(calculateDisplayingPages(pages));
    }, [
        size,
        totalCount,
        page
    ]);

    const options = rowsPerPage.map((number: number) => ({
        label: number.toString(),
        value: number
    }));

    return (
        <>
            <Grid
                container
                justify="space-between"
                alignItems="center"
            >
                <Grid
                    container
                    item
                    alignItems="center"
                    xs={4}
                >
                    <Typography
                        className="ui-Pagination-label"
                    >
                        Registros por página
                    </Typography>
                    <SelectInput
                        value={options.find(({ value }: SelectOption) => value === size)}
                        isClearable={false}
                        xs={1}
                        options={options}
                        onChange={value => {
                            handleSizeChange(value);
                            handlePageChange(1);
                        }}
                    />
                </Grid>
                <Grid
                    container
                    alignItems="flex-start"
                    justify="flex-end"
                    item
                    xs={5}
                >
                    <Grid
                        item
                        xs={3}
                    >
                        <Typography
                            className="ui-Pagination-totalcount-label"
                        >
                            {`${totalCount} registro${totalCount !== 1 ? 's' : ''}`}
                        </Typography>
                    </Grid>
                    { totalCount > size && (
                        <>
                            <IconButton
                                disableRipple
                                className="ui-Pagination-button pagination-nav-icon"
                                disabled={page <= 1}
                                onClick={() => handlePageChange(page - 1)}
                            >
                                <ChevronBigLeft
                                    color={page <= 1 ? undefined : VARIABLES.palette.secondary_color}
                                />
                            </IconButton>
                            { displayingPages.map((currentPage: number) => (
                                <IconButton
                                    key={currentPage}
                                    disableRipple
                                    className={`ui-Pagination-button${page === currentPage ? ' active-page' : ''}`}
                                    disabled={page === currentPage}
                                    onClick={() => handlePageChange(currentPage)}
                                >
                                    {currentPage}
                                </IconButton>
                            ))}
                            <IconButton
                                disableRipple
                                className="ui-Pagination-button pagination-nav-icon"
                                disabled={page === displayingPages[displayingPages.length - 1]}
                                onClick={() => handlePageChange(page + 1)}
                            >
                                <ChevronBigRight
                                    color={page === displayingPages[displayingPages.length - 1] ? undefined : VARIABLES.palette.secondary_color}
                                />
                            </IconButton>
                        </>
                    )}
                </Grid>
            </Grid>
        </>
    );
};

export default Pagination;
